/**
 * "Did You Know?" card — the long per-team interesting fact, split out of the
 * Team Comparison table so it has room to breathe. One short paragraph per team,
 * labelled by team name. Renders nothing when neither team has a fact.
 */

import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { SectionCard } from '../SectionCard';
import { useTheme } from '../../../src/theme/useTheme';
import type { MatchHubMockData } from '../../../src/match/types/matchHub.types';
import { getMatchHubTeamFactNotesStyles } from './MatchHubTeamFactNotesCard.styles';
import { MATCH_HUB_TEAM_FACTS_COPY, MATCH_HUB_TEAM_FACTS_TEST_IDS } from './matchHubTeamFacts.constants';

export interface MatchHubTeamFactNotesCardProps {
  data: MatchHubMockData;
}

export const MatchHubTeamFactNotesCard: React.FC<MatchHubTeamFactNotesCardProps> = ({ data }) => {
  const theme = useTheme();
  const styles = useMemo(() => getMatchHubTeamFactNotesStyles(theme), [theme]);

  const notes = useMemo(
    () =>
      [
        { name: data.home.shortName, fact: data.teamFacts?.home?.interestingFact },
        { name: data.away.shortName, fact: data.teamFacts?.away?.interestingFact },
      ].filter((n): n is { name: string; fact: string } => Boolean(n.fact)),
    [data.home.shortName, data.away.shortName, data.teamFacts?.home, data.teamFacts?.away],
  );

  if (notes.length === 0) return null;

  return (
    <SectionCard title={MATCH_HUB_TEAM_FACTS_COPY.NOTES_TITLE} testID={MATCH_HUB_TEAM_FACTS_TEST_IDS.notes}>
      {notes.map((note) => (
        <View key={note.name} style={styles.note}>
          <Text style={styles.team}>{note.name}</Text>
          <Text style={styles.fact}>{note.fact}</Text>
        </View>
      ))}
    </SectionCard>
  );
};
